import React, { useEffect, useState } from 'react';
import {
	Box,
	Typography,
	useMediaQuery,
	Button,
	Avatar,
	Link,
} from '@mui/material';
import WhyShare from './Sections/WhyShare';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { toast } from 'react-toastify';
import AuthApis from '../hepler/authApis.service';
import { exitStyles } from './NoAccountFoundExit.styles';

const Exit = (props) => {
	const [timer, setTimer] = useState(10);
	const [hasSessionError, setHasSessionError] = useState(false);
	const theme = useTheme();
	const authApis = new AuthApis();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('md'));
	const desktopScreen = useMediaQuery(theme.breakpoints.up('lg'));
	const location = useLocation();
	const { fipIds = [] } = location.state || {};
	
	// Check for dark theme
	const isDark = props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	useEffect(() => {
		const interval = setInterval(() => {
			setTimer((prevTimer) => {
				if (prevTimer <= 1) {
					clearInterval(interval);
					handleRedirect();
					return 0;
				}
				return prevTimer - 1;
			});
		}, 1000);

		return () => clearInterval(interval);
	}, []);

	const handleRedirectWebApp = () => {
		window.open('https://aaweb.finvu.in/', '_blank');
	};

	const handleRedirect = async () => {
		try {
			let code = await authApis.handleCancelTermination('cancel');

			const encryptResponse = await authApis.encryptRequest(code);

			// Check for session error in encryptRequest
			if (
				encryptResponse?.status === 'FAILURE' &&
				encryptResponse?.message === 'Session Error.'
			) {
				toast.error(encryptResponse?.message);
				setHasSessionError(true);
				return;
			}

			let payload = encryptResponse?.payload;

			const logoutResponse = await authApis.logout(global.userId);

			// Check for session error in logout
			if (
				logoutResponse?.status === 'FAILURE' &&
				logoutResponse?.message === 'Session Error.'
			) {
				toast.error(encryptResponse?.message);
				setHasSessionError(true);
				return;
			}

			if (logoutResponse?.status === 'SUCCESS') {
				if (payload['redirectUrl'] && payload['redirectUrl'] !== 'none') {
					if (payload['redirectUrl'].includes('?')) {
						window.location.replace(
							payload['redirectUrl'] +
								'&ecres=' +
								payload['encryptedResponse'] +
								'&resdate=' +
								payload['responseDate'] +
								'&fi=' +
								payload['encryptedFiId']
						);
					} else {
						window.location.replace(
							payload['redirectUrl'] +
								'?ecres=' +
								payload['encryptedResponse'] +
								'&resdate=' +
								payload['responseDate'] +
								'&fi=' +
								payload['encryptedFiId']
						);
					}
				}
			} else {
				console.error(logoutResponse?.message);
				toast.error(logoutResponse?.message);
			}
		} catch (error) {
			console.error('Error during redirect:', error);
			setHasSessionError(true);
		}
	};

	if (hasSessionError) {
		return (
			<Box sx={exitStyles.mainContainer(mobileScreen, isDark)}>
				<Box sx={exitStyles.contentBox(mobileScreen, desktopScreen, isDark)}>
					<Typography variant="h6" sx={exitStyles.errorText(isDark)}>
						Something Went Wrong
					</Typography>
					<Typography sx={{ 
						textAlign: 'center', 
						mb: '0.75rem',
						color: `${isDark ? '#FFFFFF' : '#000000'} !important`,
						'&.MuiTypography-root': {
							color: `${isDark ? '#FFFFFF' : '#000000'} !important`,
						}
					}}>
						Download FINVU App or visit &nbsp;
						<Link
							underline={'none'}
							onClick={handleRedirectWebApp}
							sx={{ 
								cursor: 'pointer',
							}}
						>
							Finvu Web App &nbsp;
						</Link>
						to manage consents and more
					</Typography>
					<Typography variant="body1" sx={{ 
						mb: 4, 
						textAlign: 'center',
						color: `${isDark ? '#FFFFFF' : '#000000'} !important`,
						'&.MuiTypography-root': {
							color: `${isDark ? '#FFFFFF' : '#000000'} !important`,
						}
					}}>
						You may close the window now.
					</Typography>
				</Box>
				<Box sx={exitStyles.footerContainer(isDark)}>
					<WhyShare />
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={exitStyles.mainContainer(mobileScreen, isDark)}>
			<Box sx={exitStyles.contentBox(mobileScreen, desktopScreen, isDark)}>
				<Box sx={exitStyles.logosContainer(mobileScreen)}>
					{fipIds.length > 0 && (
						<>
							{fipIds.slice(0, 5).map((fipId, index) => {
								const logoUrl = global.objectFormatFip[fipId]?.productIconUri;
								const fallbackUrl = 'https://finvu.in/cdn/finvu_bank_icon.png';

								return (
									<Avatar
										key={index}
										alt={global.objectFormatFip[fipId]?.fipName || 'FIP Logo'}
										src={logoUrl || fallbackUrl}
										imgProps={{
											onError: (e) => {
												if (e.target.src !== fallbackUrl) {
													e.target.src = fallbackUrl;
												}
											},
										}}
										sx={exitStyles.fipAvatar(index, fipIds.length, isDark)}
									/>
								);
							})}
							{fipIds.length > 5 && (
								<Avatar sx={exitStyles.moreAvatar(isDark)}>
									+{fipIds.length - 5} more
								</Avatar>
							)}
						</>
					)}
				</Box>
				<Typography variant="h6" sx={exitStyles.titleText(isDark)}>
					No Accounts Found
				</Typography>
				<Typography variant="body1" sx={exitStyles.bodyText(isDark)}>
					You do not have any accounts linked to the mobile number & provided
					PAN.
				</Typography>
				{desktopScreen && <Box sx={{ flexGrow: 1 }} />}
				<Box sx={exitStyles.buttonContainer(mobileScreen)}>
					<Button
						variant="outlined"
						color="primary"
						sx={exitStyles.goBackButton(
							mobileScreen,
							props.customDetails?.primaryColor
						)}
						onClick={handleRedirect}
					>
						<Typography>Go Back to {global.entityName}</Typography>
					</Button>
					<Typography sx={{ 
						color: `${isDark ? '#FFFFFF' : '#000000'} !important`,
						'&.MuiTypography-root': {
							color: `${isDark ? '#FFFFFF' : '#000000'} !important`,
						}
					}}>
						This page will redirect back in {timer} ...
					</Typography>
				</Box>
			</Box>
			<Box sx={exitStyles.footerContainer(isDark)}>
				<WhyShare />
			</Box>
		</Box>
	);
};

export default Exit;
